/**
 * ValidationResults - Split-pane results view.
 *
 * Top: inline status chips + Download XLSX + "Show only issues" toggle.
 * Left rail: searchable list of (FP x system) rows with status badges.
 * Right pane: entity-level comparison table for the currently-selected pair.
 *
 * No accordions, no nested expand/collapse - the whole grid fits on one screen.
 */
import React, { useEffect, useMemo, useState } from 'react'
import {
  Box,
  Paper,
  Typography,
  Grid,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  InputAdornment,
  Switch,
  FormControlLabel,
  Chip,
  Tooltip,
  List,
  ListItemButton,
  Divider,
} from '@mui/material'
import {
  GetApp as ExportIcon,
  Search as SearchIcon,
  CheckCircle as PerfectIcon,
  Warning as WarningIcon,
  Error as ErrorIcon,
} from '@mui/icons-material'

import { getSystemIcon } from '../../../../../components/SystemIcons'
import { ValidationStatus, ValidationResult } from '../types'

interface ValidationResultsProps {
  validationStatus: ValidationStatus
  onExport: () => void | Promise<void>
}

type Severity = 'perfect' | 'close' | 'warning' | 'error'

interface FlatResult extends ValidationResult {
  fp_code: string
  fp_name: string
  system: string
  severity: Severity
}

interface PairKey {
  fp_code: string
  system: string
}

interface PairRow extends PairKey {
  fp_name: string
  results: FlatResult[]
  worst: Severity
  issueCount: number
  overallMatchPct: number | null
}

const severityRank: Record<Severity, number> = { error: 0, warning: 1, close: 2, perfect: 3 }
const worseOf = (a: Severity, b: Severity): Severity =>
  severityRank[a] < severityRank[b] ? a : b

const classifyStatus = (status: string): Severity => {
  const s = String(status || '').toLowerCase()
  if (status === '✅' || s === 'perfect' || s === 'match') return 'perfect'
  if (status === '✔️' || s === 'close') return 'close'
  if (status === '⚠️' || s === 'warning') return 'warning'
  return 'error'
}

const severityChip = (severity: Severity, size: 'small' | 'medium' = 'small') => {
  switch (severity) {
    case 'perfect':
      return <Chip size={size} icon={<PerfectIcon />} label="Match" color="success" variant="outlined" />
    case 'close':
      return <Chip size={size} icon={<PerfectIcon />} label="Close" color="info" variant="outlined" />
    case 'warning':
      return <Chip size={size} icon={<WarningIcon />} label="Warning" color="warning" />
    case 'error':
      return <Chip size={size} icon={<ErrorIcon />} label="Error" color="error" />
  }
}

const severityColors: Record<Severity, string> = {
  perfect: 'success.main',
  close: 'info.main',
  warning: 'warning.main',
  error: 'error.main',
}

const overallMatchChip = (severity: Severity, pct: number | null) => (
  <Chip
    size="small"
    label={pct === null ? '-' : `${pct.toFixed(1)}%`}
    sx={{
      bgcolor: severityColors[severity],
      color: 'common.white',
      fontWeight: 600,
      fontFamily: 'monospace',
      height: 20,
      flexShrink: 0,
      '& .MuiChip-label': { px: 0.75 },
    }}
  />
)

const computeOverallMatch = (results: FlatResult[]): number | null => {
  let totalApi = 0
  let weighted = 0
  for (const r of results) {
    if (typeof r.api_count !== 'number' || r.api_count <= 0) continue
    if (typeof r.match_percentage !== 'number') continue
    totalApi += r.api_count
    weighted += r.api_count * r.match_percentage
  }
  if (totalApi === 0) return null
  return weighted / totalApi
}

const formatNumber = (value: number | null | undefined): string => {
  if (value === null || value === undefined) return '-'
  return value.toLocaleString()
}

const formatDiff = (diff: number | string | null | undefined): string => {
  if (diff === null || diff === undefined || diff === '') return '-'
  if (typeof diff === 'number') {
    if (diff === 0) return '0'
    return diff > 0 ? `+${diff.toLocaleString()}` : diff.toLocaleString()
  }
  return String(diff)
}

const formatPct = (value: number | string | null | undefined): string => {
  if (value === null || value === undefined) return '-'
  if (typeof value === 'number') return `${value.toFixed(1)}%`
  return String(value)
}

const titleCase = (s: string) => s.replace(/_/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase())

const ValidationResults: React.FC<ValidationResultsProps> = ({ validationStatus, onExport }) => {
  const [searchTerm, setSearchTerm] = useState('')
  const [showOnlyIssues, setShowOnlyIssues] = useState(false)
  const [selectedPair, setSelectedPair] = useState<PairKey | null>(null)

  const allRows: FlatResult[] = useMemo(() => {
    const rows: FlatResult[] = []
    Object.entries(validationStatus.results).forEach(([fpCode, systems]) => {
      Object.entries(systems).forEach(([systemName, systemData]) => {
        systemData.results.forEach((r) => {
          rows.push({
            ...r,
            fp_code: fpCode,
            fp_name: systemData.fp_name || fpCode,
            system: systemName,
            severity: classifyStatus(r.status),
          })
        })
      })
    })
    return rows
  }, [validationStatus.results])

  const pairRows: PairRow[] = useMemo(() => {
    const groups = new Map<string, PairRow>()
    for (const r of allRows) {
      const key = `${r.fp_code}::${r.system}`
      let g = groups.get(key)
      if (!g) {
        g = {
          fp_code: r.fp_code,
          fp_name: r.fp_name,
          system: r.system,
          results: [],
          worst: 'perfect',
          issueCount: 0,
          overallMatchPct: null,
        }
        groups.set(key, g)
      }
      g.results.push(r)
      g.worst = worseOf(g.worst, r.severity)
      if (r.severity === 'warning' || r.severity === 'error') g.issueCount++
    }
    for (const g of groups.values()) {
      g.overallMatchPct = computeOverallMatch(g.results)
    }
    return Array.from(groups.values()).sort((a, b) => {
      const cmp = severityRank[a.worst] - severityRank[b.worst]
      if (cmp !== 0) return cmp
      const nameCmp = a.fp_name.localeCompare(b.fp_name)
      if (nameCmp !== 0) return nameCmp
      return a.system.localeCompare(b.system)
    })
  }, [allRows])

  const filteredPairs = useMemo(() => {
    return pairRows.filter((p) => {
      if (showOnlyIssues && p.issueCount === 0) return false
      if (searchTerm) {
        const q = searchTerm.toLowerCase()
        const hay = `${p.fp_name} ${p.fp_code} ${p.system}`.toLowerCase()
        if (!hay.includes(q)) return false
      }
      return true
    })
  }, [pairRows, showOnlyIssues, searchTerm])

  // Auto-select the first pair (sorted worst-first) when filters change.
  useEffect(() => {
    if (filteredPairs.length === 0) {
      setSelectedPair(null)
      return
    }
    const stillValid = selectedPair && filteredPairs.some(
      (p) => p.fp_code === selectedPair.fp_code && p.system === selectedPair.system,
    )
    if (!stillValid) {
      setSelectedPair({ fp_code: filteredPairs[0].fp_code, system: filteredPairs[0].system })
    }
  }, [filteredPairs, selectedPair])

  const totals = useMemo(() => {
    let perfect = 0
    let close = 0
    let warning = 0
    let error = 0
    for (const r of allRows) {
      if (r.severity === 'perfect') perfect++
      else if (r.severity === 'close') close++
      else if (r.severity === 'warning') warning++
      else error++
    }
    return { perfect, close, warning, error, total: allRows.length }
  }, [allRows])

  const selectedRow = useMemo(() => {
    if (!selectedPair) return null
    return pairRows.find(
      (p) => p.fp_code === selectedPair.fp_code && p.system === selectedPair.system,
    ) ?? null
  }, [pairRows, selectedPair])

  return (
    <Paper sx={{ p: 0, overflow: 'hidden' }}>
      <Box sx={{ p: 2, display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 600, mr: 1 }}>
          Results
        </Typography>
        <Chip size="small" icon={<PerfectIcon />} label={`Match ${totals.perfect}`} color="success" variant="outlined" />
        <Chip size="small" icon={<PerfectIcon />} label={`Close ${totals.close}`} color="info" variant="outlined" />
        <Chip size="small" icon={<WarningIcon />} label={`Warning ${totals.warning}`} color="warning" />
        <Chip size="small" icon={<ErrorIcon />} label={`Error ${totals.error}`} color="error" />
        <Box sx={{ flexGrow: 1 }} />
        <FormControlLabel
          control={
            <Switch
              checked={showOnlyIssues}
              onChange={(e) => setShowOnlyIssues(e.target.checked)}
              color="warning"
              size="small"
            />
          }
          label={<Typography variant="body2">Show only issues</Typography>}
        />
        <Button
          startIcon={<ExportIcon />}
          onClick={() => void onExport()}
          variant="outlined"
          size="small"
        >
          Download XLSX
        </Button>
      </Box>
      <Divider />
      <Grid container sx={{ minHeight: 480 }}>
        <Grid item xs={12} md={4} sx={{ borderRight: { md: 1 }, borderColor: { md: 'divider' } }}>
          <Box sx={{ p: 1.5 }}>
            <TextField
              fullWidth
              size="small"
              placeholder="Search FP or system..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" />
                  </InputAdornment>
                ),
              }}
            />
          </Box>
          <Divider />
          <Box sx={{ maxHeight: 640, overflowY: 'auto' }}>
            {filteredPairs.length === 0 ? (
              <Box sx={{ p: 3, textAlign: 'center' }}>
                <Typography variant="body2" color="textSecondary">
                  {allRows.length === 0
                    ? 'No results yet.'
                    : showOnlyIssues
                    ? 'No issues. Toggle "Show only issues" off to see all.'
                    : 'No results match your search.'}
                </Typography>
              </Box>
            ) : (
              <List dense disablePadding>
                {filteredPairs.map((p) => {
                  const isSelected =
                    selectedPair?.fp_code === p.fp_code && selectedPair?.system === p.system
                  return (
                    <ListItemButton
                      key={`${p.fp_code}::${p.system}`}
                      selected={isSelected}
                      onClick={() => setSelectedPair({ fp_code: p.fp_code, system: p.system })}
                      sx={{ py: 0.25, px: 1.25, gap: 1, minHeight: 0 }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', flexShrink: 0 }}>
                        {getSystemIcon(p.system)}
                      </Box>
                      <Typography
                        variant="caption"
                        color="textSecondary"
                        sx={{ fontFamily: 'monospace', flexShrink: 0 }}
                      >
                        {p.fp_code}
                      </Typography>
                      <Typography variant="body2" sx={{ minWidth: 0, flexGrow: 1 }} noWrap>
                        {p.fp_name}
                      </Typography>
                      {overallMatchChip(p.worst, p.overallMatchPct)}
                    </ListItemButton>
                  )
                })}
              </List>
            )}
          </Box>
        </Grid>
        <Grid item xs={12} md={8}>
          {selectedRow ? (
            <Box>
              <Box sx={{ p: 2, display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
                {getSystemIcon(selectedRow.system)}
                <Typography variant="subtitle1" fontWeight={600}>
                  {selectedRow.fp_name}
                </Typography>
                <Typography variant="caption" color="textSecondary" sx={{ fontFamily: 'monospace' }}>
                  {selectedRow.fp_code} - {selectedRow.system}
                </Typography>
                <Box sx={{ flexGrow: 1 }} />
                {severityChip(selectedRow.worst, 'medium')}
              </Box>
              <Divider />
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Entity</TableCell>
                      <TableCell align="right">API</TableCell>
                      <TableCell align="right">DB</TableCell>
                      <TableCell align="right">Diff</TableCell>
                      <TableCell align="right">Match %</TableCell>
                      <TableCell>Status</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {selectedRow.results.map((r) => {
                      const dbActive = (r.db_total ?? 0) - (r.db_deleted ?? 0)
                      const dbHasValue = r.db_total !== null && r.db_total !== undefined
                      const diffNumeric = typeof r.diff === 'number' ? r.diff : null
                      const diffColor =
                        diffNumeric === null
                          ? 'text.secondary'
                          : diffNumeric === 0
                          ? 'text.primary'
                          : diffNumeric < 0
                          ? 'error.main'
                          : 'success.main'
                      return (
                        <TableRow key={r.entity} hover>
                          <TableCell>
                            <Typography variant="body2">{titleCase(r.entity)}</Typography>
                            {r.error_message ? (
                              <Tooltip arrow title={r.error_message}>
                                <Typography variant="caption" color="error.main" noWrap>
                                  {r.error_message}
                                </Typography>
                              </Tooltip>
                            ) : null}
                          </TableCell>
                          <TableCell align="right" sx={{ fontFamily: 'monospace' }}>
                            {formatNumber(r.api_count)}
                          </TableCell>
                          <TableCell align="right" sx={{ fontFamily: 'monospace' }}>
                            {dbHasValue ? formatNumber(dbActive) : '-'}
                          </TableCell>
                          <TableCell align="right" sx={{ fontFamily: 'monospace', color: diffColor }}>
                            {formatDiff(r.diff)}
                          </TableCell>
                          <TableCell align="right" sx={{ fontFamily: 'monospace' }}>
                            {formatPct(r.match_percentage)}
                          </TableCell>
                          <TableCell>{severityChip(r.severity)}</TableCell>
                        </TableRow>
                      )
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          ) : (
            <Box sx={{ p: 4, textAlign: 'center' }}>
              <Typography variant="body2" color="textSecondary">
                Select an FP - system pair on the left to see entity details.
              </Typography>
            </Box>
          )}
        </Grid>
      </Grid>
    </Paper>
  )
}

export default ValidationResults
